/* @flow */
/**
 * Application type definitions: contains all possible types.
 * The types contained in this file should not be changed lightly as they are stored on the CMR (kwanza) and user by other software (e.g. Manatee) which expects them to look a certain way.
 *
 * @instance
 * @memberof ApplicationService
 */
import _ from 'lodash'
import Typenames from './Typenames'
import { User } from 'services'
import { ActionTypes, store } from 'store'

const predefinedApplicationTypeDefs = _.filter(
  [
    { type: 'WEB-CHROME', icon: 'chrome' },
    { type: 'WEB-FF', icon: 'firefox' },
    { type: 'WEB-EDGE', icon: 'edge' },
    { type: 'WEB-IE', icon: 'internet explorer' },
    { type: 'WEB-IE-PROXY', icon: 'internet explorer', color: 'green' },
    { type: 'WEB-HIE', icon: 'internet explorer', color: '#cc0000' },
    { type: 'WEB-HCHROME', icon: 'chrome', color: '#cc0000' },
    { type: 'JAVA-AWT', icon: 'coffee' },
    { type: 'HEADLESS', icon: 'headphones' },
    { type: 'NATIVE', icon: 'windows' },
    { type: 'CONTEXTPARTICIPANT', icon: 'copyright' }
  ],
  e => {
    return !_.includes(
      _.split((window.env && window.env['CUESTA_OMITTED_TYPES']) || '', ','),
      e.type
    )
  }
)

export type ApplicationType = {
  type: string,
  icon: string,
  color?: string
}

/**
 * Application: contains fields & flows, can be grouped.
 * @typedef { Object } Application
 * @memberof ApplicationService
 */
export type Application = {
  name: string,
  launchwith: string,
  jvmlocation?: string,
  launchtimeout?: number,
  appVersion?: string,
  version?: string,
  launchwitharguments?: string,
  workingdirectory?: string,
  manufacturer?: string,
  titlebarcontains?: string,
  framecontains?: string,
  urlcontains?: string,
  processcontains?: string,
  executablepathcontains?: string,
  md5: string,
  type: string,
  icon: string,
  openIn?: string,
  hideFromUser?: boolean,
  isEmbedded?: boolean,
  useCom?: boolean,
  disableKnownExecutable?: boolean,
  popupHandling?: string,
  killOnDetach?: boolean,
  switches?: string,
  launchWithFlowId?: string
}

/**
 * Application typename
 * @private
 * @memberof ApplicationService
 */
const TYPENAME = Typenames.APP

/**
 * Applicationtype typename
 * @private
 * @memberof ApplicationService
 */
const APPTYPENAME = Typenames.APPTYPE

/**
 * Returns an ApplicationType given its typename
 * @function
 * @memberof ApplicationService
 *
 * @return { Promise<ApplicationType> } The application-type.
 */
const getApplicationType = (type: string): Promise<ApplicationType | null> => {
  return getAllApplicationTypes().then(types => _.find(types, { type: type }) || null)
}

/**
 * Returns all predefined and configured ApplicationTypes
 * @function
 * @memberof ApplicationService
 *
 * @return { Promise<Array<ApplicationType>> } The application-types.
 */
const getAllApplicationTypes = (): Promise<Array<ApplicationType>> => {
  return store
    .dispatch(ActionTypes.GET, APPTYPENAME)
    .then(types => _.map(types, t => ({ ...t, type: t.id, id: t.id, name: t.name })))
    .then(types => _.concat(predefinedApplicationTypeDefs, types))
}

/**
 * Validates a passed object against the required Application type properties
 * @function
 * @memberof ApplicationService
 *
 * @return { boolean } The information if the passed object contains all the required Application properties
 */
const validateAppObject = (app?: Object): boolean => {
  return ['name', 'launchwith', 'version', 'md5', 'type', 'icon'].every(k => _.has(app, k))
}

/**
 * Creates a new application.
 * @function
 * @memberof ApplicationService
 *
 * @return { Promise<string> } The id of the added app.
 */
const createApp = (app: Application): Promise<PostResponse> => {
  return store.dispatch(ActionTypes.CREATE, { typename: TYPENAME, item: app }).then(r => {
    User.addEdit({ type: TYPENAME, id: r.id, name: app.name, routeParams: { appId: r.id } })
    return r
  })
}

/**
 * Reads an application, flattening 'descriptive'.
 * @function
 * @memberof ApplicationService
 *
 * @return { Promise<Application> } The application.
 */
const readApp = (id: string): Promise<Application> => {
  return store.dispatch(ActionTypes.GET_BY_ID, { typename: TYPENAME, id: id })
}

/**
 * Updates an application.
 * @function
 * @memberof ApplicationService
 *
 * @return { Promise<any> }
 */
const updateApp = (id: string, app: Application): Promise<PostResponse> => {
  return store.dispatch(ActionTypes.UPDATE, { typename: TYPENAME, id: id, item: app }).then(r => {
    User.addEdit({ type: TYPENAME, id, name: app.name, routeParams: { appId: id } })
    return r
  })
}

/**
 * Deletes an application.
 * @function
 * @memberof ApplicationService
 *
 * @return { Promise<any> }
 */
const deleteApp = (id: string): Promise<any> => {
  return store.dispatch(ActionTypes.DELETE, { typename: TYPENAME, id: id })
}

/**
 * Reads all applications, flattening descriptive.
 * @function
 * @memberof ApplicationService
 *
 * @return { Observable<Array<Application>> } All the applications.
 */
const readAllApps = (): Promise<Array<Application>> => {
  return store.dispatch(ActionTypes.GET, TYPENAME)
}

/**
 * Copies an app.
 * @function
 * @memberof AppService
 *
 * @return { Promise<string> }
 */
const copyApp = (id: string, copyName: string): Promise<PostResponse> => {
  return self.readApp(id).then(app => {
    const appToCopy: Object = { ...app, name: copyName }
    delete appToCopy.id

    return self.createApp(appToCopy)
  })
}

/**
 * Reads all apps that use a manufacturer.
 * @function
 * @memberof ManufacturerService
 *
 * @return { Observable<Array<Application>> } Apps that the manufacturer is used in.
 */
const readAppsUsingManufacturer = (manufacturerId: string): Promise<Array<Application>> => {
  return readAllApps().then(apps => {
    return _.filter(apps, app => app.manufacturer === manufacturerId)
  })
}

const self = {
  createApp,
  readApp,
  updateApp,
  deleteApp,
  readAllApps,
  copyApp,
  validateAppObject,
  getApplicationType,
  getAllApplicationTypes,
  readAppsUsingManufacturer
}

/**
 * Application service: CRUD interface for apps.
 * @module ApplicationService
 * @namespace ApplicationService
 * @version 1.0.0
 */
export default self
